import parse from "html-react-parser";
import { valid as isValidHTML } from "node-html-parser";
import { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { LuChevronDown, LuCopy } from "react-icons/lu";
import { colors, typography, effects } from "../../../../../design/tokens";
import { useSnackbar } from "../../../../../providers/SnackbarProvider";
import { typewriterWorkerService } from "../../../../../services/typewriterWorkerService";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
} from "../../../../../view_stores/chat_messages/chatModels";
import { useChatMessagesActions } from "../../../../../view_stores/chat_messages/useChatMessagesStore";
import { typewriterUpdateEmitter } from "../../ProjectChatMessages";

interface ExpandableMessageProps {
  message: string;
  icon?: React.ReactNode;
  expandBtnText: {
    contentHiddenText: string;
    contentVisibleText: string;
  };
  canCopy: boolean;
}

interface ProjectAssistantMessageBaseProps {
  message: ProjectChatAssistantMessage;
  typeWriterAnimationSpeed: number;
  words: string[];
  renderContent: (visibleWords: number) => React.ReactNode;
  expandableMessage?: ExpandableMessageProps;
  actions?: React.ReactNode;
}

export function ProjectAssistantMessageBase({
  message,
  typeWriterAnimationSpeed,
  words,
  renderContent,
  expandableMessage,
  actions,
}: ProjectAssistantMessageBaseProps) {
  const [visibleWords, setVisibleWords] = useState(message.visibleWords);
  const [isExpanded, setIsExpanded] = useState(false);
  const { onAssistantMessageAnimationFinished } = useChatMessagesActions();
  const { showSnackbar } = useSnackbar();

  // Run animation when message is in ANIMATING status
  useEffect(() => {
    if (message.animationStatus === AssistantMessageAnimationStatus.ANIMATING) {
      typewriterWorkerService.startAnimation(
        words.length,
        typeWriterAnimationSpeed,
        {
          onWordUpdate: (currentWordIndex: number) => {
            message.visibleWords = currentWordIndex;
            setVisibleWords(currentWordIndex);
            typewriterUpdateEmitter.emit();
          },
          onComplete: () => {
            // Only finish animation if message is fully generated
            if (message.fullyGenerated) {
              onAssistantMessageAnimationFinished();
            }
            message.visibleWords = words.length;
            setVisibleWords(words.length);
          },
        },
        message.visibleWords
      );
    } else if (
      message.animationStatus === AssistantMessageAnimationStatus.DISPLAYED
    ) {
      message.visibleWords = words.length;
      setVisibleWords(words.length);
    }
  }, [message.animationStatus, message.fullyGenerated, words]);

  // Cleanup animation when component unmounts or message changes
  useEffect(() => {
    return () => {
      // Only stop animation if this message is currently animating
      if (
        message.animationStatus === AssistantMessageAnimationStatus.ANIMATING
      ) {
        typewriterWorkerService.stopAnimation();
      }
    };
  }, [message.id, message.animationStatus]);

  const handleCopy = () => {
    showSnackbar("Copied to clipboard");
  };

  // Don't render anything if the message is waiting to be animated
  if (message.animationStatus === AssistantMessageAnimationStatus.WAITING) {
    return null;
  }

  return (
    <div>
      <div
        className={`${
          message.animationStatus ===
            AssistantMessageAnimationStatus.ANIMATING &&
          "message-container assistant-container"
        } w-full flex justify-start`}
      >
        <div
          className={`w-full flex flex-col justify-start
          break-words hyphens-auto relative
          ${typography.size.sm}`}
        >
          <div className="py-1 overflow-hidden">
            <div className="whitespace-pre-wrap break-words hyphens-auto w-full react-markdown leading-[1.6]">
              {renderContent(visibleWords)}
            </div>

            {expandableMessage &&
              message.animationStatus ===
                AssistantMessageAnimationStatus.DISPLAYED && (
                <div
                  className={`bg-dark-surface-secondary ${colors.border.primary}
            border ${effects.borderRadius.lg} mt-4 overflow-hidden`}
                >
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`w-full flex items-center justify-between
                  gap-1 p-4 ${typography.size.sm} ${isExpanded && "bg-black/20"}`}
                  >
                    <div className="flex items-center gap-4">
                      {expandableMessage.icon}
                      <span className={colors.text.secondary}>
                        {isExpanded
                          ? expandableMessage.expandBtnText.contentVisibleText
                          : expandableMessage.expandBtnText.contentHiddenText}
                      </span>
                    </div>
                    <LuChevronDown
                      size={24}
                      className={`${effects.transition.transform} ${
                        isExpanded ? "-rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-y-auto overflow-x-hidden
                  transition-all duration-500 ease-in-out mx-4 ${
                    isExpanded
                      ? "max-h-[1000px] opacity-100 my-4"
                      : "max-h-0 opacity-0 my-0"
                  }`}
                  >
                    <div className={`flex items-start gap-4 w-full min-w-0 ${typography.size.sm}`}>
                      <span className="break-words hyphens-auto whitespace-pre-wrap flex-1 min-w-0">
                        {isValidHTML(expandableMessage.message)
                          ? parse(expandableMessage.message)
                          : expandableMessage.message}
                      </span>
                      {expandableMessage.canCopy && (
                        <div className="pt-1 flex-shrink-0">
                          <CopyToClipboard
                            text={expandableMessage.message}
                            onCopy={handleCopy}
                          >
                            <LuCopy
                              size={20}
                              className={`hover:cursor-pointer ${colors.hover.opacity} ${effects.transition.opacity}`}
                            />
                          </CopyToClipboard>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {actions &&
              message.animationStatus ===
                AssistantMessageAnimationStatus.DISPLAYED && (
                <div className="mt-4">{actions}</div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
